import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  CancelDowngradeSubscriptionPlanProcess,
  CancelDowngradeSubscriptionPlanProcessMutation,
  CancelDowngradeSubscriptionPlanProcessMutationVariables,
  DowngradeSubscriptionPlanProcess,
  DowngradeSubscriptionPlanProcessMutation,
  DowngradeSubscriptionPlanProcessMutationVariables,
  MembershipsQuery,
  MembershipsQueryVariables,
  PatronDetailFragmentDoc,
  PreviewSubscriptionUpdate,
  PreviewSubscriptionUpdateQuery,
  PreviewSubscriptionUpdateQueryVariables,
  ResumeSubscriptionPlanProcessMutation,
  ResumeSubscriptionPlanProcessMutationVariables,
  StopSubscriptionProcessMutation,
  StopSubscriptionProcessMutationVariables,
  UpgradeSubscriptionPlanProcess,
  UpgradeSubscriptionPlanProcessMutation,
  UpgradeSubscriptionPlanProcessMutationVariables,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import {
  isCarrierPaymentMethod,
  submitEscottForm,
  useBFCacheRestore,
} from '@/lib/escott'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showModal } = useSettingUserInfoModal()
  const { loading, data } = useQuery<
    MembershipsQuery,
    MembershipsQueryVariables
  >(MEMBERSHIPS)

  const { user } = data || {}
  const { organizationPatrons: memberships } = user || {}

  const [previewSubscriptionUpdate, { data: previewSubscriptionUpdateData }] =
    useLazyQuery<
      PreviewSubscriptionUpdateQuery,
      PreviewSubscriptionUpdateQueryVariables
    >(PreviewSubscriptionUpdate)

  // 外部キャリア決済画面（escott）へのリダイレクト中かどうかを管理するフラグ
  // mutation の完了から escott フォーム送信までに
  // submit ボタンが活性化してしまうのを防ぐために用いている
  const [isRedirectingToEscott, setIsRedirectingToEscott] = useState(false)

  const [stopSubscriptionProcess, { loading: stoppingSubscription }] =
    useMutation<
      StopSubscriptionProcessMutation,
      StopSubscriptionProcessMutationVariables
    >(STOP_SUBSCRIPTION_PROCESS, {
      onCompleted({ stopSubscriptionProcess }) {
        switch (stopSubscriptionProcess.__typename) {
          // 追加操作を必要とせず退会処理が完了した場合
          // ステータス更新後の Patron ドキュメントが返却される
          case 'Patron':
            if (stopSubscriptionProcess?.isPersonalInformationRequired) {
              showModal()
            }
            break
          // escott でのサブスクリプション更新プロセスが必要な場合
          // キャリア決済を想定している
          // 現状はキャリア決済であっても Patron が返ってくるのでここには到達しない見込み
          // ref. https://github.com/utoniq/utoniq-mono/blob/feature/UT-18861/api-parent/packages/utoniq-core/docs/CarrierPayment.md#-stopsubscriptionprocess
          // softbank では検証済みで、au, docomo で検証の上、不要であれば分岐ごと削除してもよい
          // もしも到達することになった場合は、復帰画面にてキャッシュの更新を行う処理を組み込む必要がある
          case 'EscottMasterResult':
            submitEscottForm(
              stopSubscriptionProcess.nextActionUrl,
              stopSubscriptionProcess.parameter
            )
            break
          default:
            break
        }
      },
    })

  const [
    upgradeSubscriptionPlanProcess,
    { loading: upgradingSubscriptionPlan },
  ] = useMutation<
    UpgradeSubscriptionPlanProcessMutation,
    UpgradeSubscriptionPlanProcessMutationVariables
  >(UpgradeSubscriptionPlanProcess, {
    onCompleted({ upgradeSubscriptionPlanProcess }) {
      switch (upgradeSubscriptionPlanProcess.__typename) {
        // 追加操作を必要とせず復帰処理が完了した場合
        // ステータス更新後の Patron ドキュメントが返却される
        case 'Patron':
          if (upgradeSubscriptionPlanProcess?.isPersonalInformationRequired) {
            showModal()
          }
          break
        // escott でのサブスクリプション更新プロセスが必要な場合
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            upgradeSubscriptionPlanProcess.nextActionUrl,
            upgradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  const [
    downgradeSubscriptionPlanProcess,
    { loading: downgradingSubscriptionPlan },
  ] = useMutation<
    DowngradeSubscriptionPlanProcessMutation,
    DowngradeSubscriptionPlanProcessMutationVariables
  >(DowngradeSubscriptionPlanProcess, {
    onCompleted({ downgradeSubscriptionPlanProcess }) {
      switch (downgradeSubscriptionPlanProcess.__typename) {
        // 追加手続きが不要なケース
        // ステータスの更新された Patron が返される
        case 'Patron':
          if (downgradeSubscriptionPlanProcess?.isPersonalInformationRequired) {
            showModal()
          }
          break
        // 外部キャリア決済画面にて追加手続きが必要なケース
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            downgradeSubscriptionPlanProcess.nextActionUrl,
            downgradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  /**
   * プラン変更のキャンセル
   * アップグレードのキャンセルもダウングレードのキャンセルも
   * API の実装としては同一の処理であるため
   * cancelDowngradeSubscriptionPlanProcess を参照している
   * ref. https://utoniq.slack.com/archives/C089QSPGB27/p1747637183291179?thread_ts=1747635734.465719&cid=C089QSPGB27
   */
  const [
    cancelChangeSubscriptionPlanProcess,
    { loading: cancellingChangeSubscription },
  ] = useMutation<
    CancelDowngradeSubscriptionPlanProcessMutation,
    CancelDowngradeSubscriptionPlanProcessMutationVariables
  >(CancelDowngradeSubscriptionPlanProcess, {
    onCompleted({ cancelDowngradeSubscriptionPlanProcess }) {
      switch (cancelDowngradeSubscriptionPlanProcess.__typename) {
        // 追加手続きが不要なケース
        // ステータスの更新された Patron が返される
        case 'Patron':
          break
        // 外部キャリア決済画面にて追加手続きが必要なケース
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            cancelDowngradeSubscriptionPlanProcess.nextActionUrl,
            cancelDowngradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  const [resumeSubscriptionPlanProcess, { loading: resumingSubscription }] =
    useMutation<
      ResumeSubscriptionPlanProcessMutation,
      ResumeSubscriptionPlanProcessMutationVariables
    >(RESUME_SUBSCRIPTION_PLAN_PROCESS, {
      onCompleted({ resumeSubscriptionPlanProcess }) {
        switch (resumeSubscriptionPlanProcess.__typename) {
          // 追加操作を必要とせず復帰処理が完了した場合
          // ステータス更新後の Patron ドキュメントが返却される
          case 'Patron':
            break
          // escott でのサブスクリプション更新プロセスが必要な場合
          // キャリア決済を想定している
          case 'EscottMasterResult':
            // 外部のキャリア決済画面へフォーム送信によってリダイレクト
            // 後続処理が外部画面とバックエンドで実行された後に
            // ステータスに応じて escottInput で指定した画面に復帰する
            submitEscottForm(
              resumeSubscriptionPlanProcess.nextActionUrl,
              resumeSubscriptionPlanProcess.parameter
            )
            break
          default:
            break
        }
      },
    })

  // 外部キャリア決済画面（escott）からブラウザバックした際
  // リダイレクト中かどうかを管理するフラグをリセットする
  // ブラウザバック時に BFCache が効き再読み込みが走らない環境で
  // ローディング状態が解除されないことを防ぐ
  useBFCacheRestore(() => {
    setIsRedirectingToEscott(false)
  })

  return {
    state: {
      loading,
      stoppingSubscription,
      changingSubscription:
        upgradingSubscriptionPlan || downgradingSubscriptionPlan,
      cancellingChangeSubscription:
        cancellingChangeSubscription || isRedirectingToEscott,
      resumingSubscription: resumingSubscription || isRedirectingToEscott,
      memberships,
      isMembershipPersonalInformationRequired:
        !!user?.isMembershipPersonalInformationRequired,
      diffAmountSubscription:
        previewSubscriptionUpdateData?.previewSubscriptionUpdate.diffAmount ??
        0,
    },
    actions: {
      async onPreviewSubscriptionUpdate(artistId, planId) {
        await previewSubscriptionUpdate({
          variables: { artistId, planId },
        })
      },
      async onStopSubscription(artistId) {
        await stopSubscriptionProcess({
          variables: { artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onUpgradeSubscriptionPlan(planId, artistId) {
        await upgradeSubscriptionPlanProcess({
          variables: { planId, artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onDowngradeSubscriptionPlan(planId, artistId) {
        await downgradeSubscriptionPlanProcess({
          variables: { planId, artistId },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onCancelChangeSubscription(artistId, paymentMethod, escottInput) {
        // キャリア決済の場合はローディング状態を表す
        // state.resumingSubscription に渡している
        // isRedirectingToEscott を true にする
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // submit ボタンが活性化するのを防ぐために行う
        if (isCarrierPaymentMethod(paymentMethod)) {
          setIsRedirectingToEscott(true)
        }
        await cancelChangeSubscriptionPlanProcess({
          variables: { artistId, paymentMethod, escottInput },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
      async onResumeSubscription(artistId, paymentMethod, escottInput) {
        // キャリア決済の場合はローディング状態を表す
        // state.resumingSubscription に渡している
        // isRedirectingToEscott を true にする
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // submit ボタンが活性化するのを防ぐために行う
        if (isCarrierPaymentMethod(paymentMethod)) {
          setIsRedirectingToEscott(true)
        }
        await resumeSubscriptionPlanProcess({
          variables: { artistId, paymentMethod, escottInput },
          refetchQueries() {
            return [{ query: MEMBERSHIPS }]
          },
          awaitRefetchQueries: true,
        })
      },
    },
  }
})

const MEMBERSHIPS = gql`
  query memberships {
    user {
      id
      isMembershipPersonalInformationRequired
      organizationPatrons {
        ...PatronDetail
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const STOP_SUBSCRIPTION_PROCESS = gql`
  mutation stopSubscriptionProcess($artistId: ID!) {
    stopSubscriptionProcess(artistId: $artistId) {
      __typename
      ... on Patron {
        id
        isPersonalInformationRequired
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const RESUME_SUBSCRIPTION_PLAN_PROCESS = gql`
  mutation resumeSubscriptionPlanProcess(
    $artistId: String!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
  ) {
    resumeSubscriptionPlanProcess(
      artistId: $artistId
      paymentMethod: $paymentMethod
      escottInput: $escottInput
    ) {
      __typename
      ... on Patron {
        id
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
  ${PatronDetailFragmentDoc}
`
